import { createCampaignUrl, trackEvent } from './lib/analytics';
import {
  cardImagePath,
  getRomeDateKey,
  getScenarioForDate,
  isChoiceLegal,
  isValidDateKey,
  type DailyHandChoice,
  type DailyHandScenario,
} from './lib/dailyHand';
import type { Card } from './types';

const params = new URLSearchParams(window.location.search);
const requestedDate = params.get('date');
const dateKey = isValidDateKey(requestedDate) ? requestedDate : getRomeDateKey();
const scenario = getScenarioForDate(dateKey);

if (requestedDate !== dateKey) {
  const canonicalUrl = new URL(window.location.href);
  canonicalUrl.searchParams.set('date', dateKey);
  window.history.replaceState({}, '', canonicalUrl);
}

const suitNames: Record<Card['suit'], string> = {
  denari: 'Denari',
  coppe: 'Coppe',
  spade: 'Spade',
  bastoni: 'Bastoni',
};

const valueNames: Record<number, string> = {
  1: 'Asso',
  8: 'Fante',
  9: 'Cavallo',
  10: 'Re',
};

function cardName(card: Card): string {
  return `${valueNames[card.value] || card.value} di ${suitNames[card.suit]}`;
}

function requiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing daily-hand element: ${id}`);
  return element as T;
}

function renderCards(target: HTMLElement, cards: Card[]): void {
  target.replaceChildren(...cards.map(card => {
    const figure = document.createElement('figure');
    figure.style.margin = '0';
    figure.style.textAlign = 'center';

    const image = document.createElement('img');
    image.src = cardImagePath(card);
    image.alt = cardName(card);
    image.width = 120;
    image.height = 198;
    image.className = 'playing-card';
    image.loading = 'eager';
    image.decoding = 'async';

    const caption = document.createElement('figcaption');
    caption.textContent = cardName(card);
    caption.style.fontSize = '0.78rem';
    caption.style.color = 'var(--muted)';
    caption.style.marginTop = '0.35rem';

    figure.append(image, caption);
    return figure;
  }));
}

interface StreakState {
  lastCorrectDate: string;
  streak: number;
}

function getPreviousDateKey(value: string): string {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

function updateStreak(correct: boolean): number {
  const storageKey = 'sbarazzino_daily_hand_streak';
  let state: StreakState = { lastCorrectDate: '', streak: 0 };

  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) state = JSON.parse(raw) as StreakState;
  } catch {
    state = { lastCorrectDate: '', streak: 0 };
  }

  if (!correct) return state.streak;
  if (state.lastCorrectDate === dateKey) return state.streak;

  const nextStreak = state.lastCorrectDate === getPreviousDateKey(dateKey)
    ? state.streak + 1
    : 1;

  localStorage.setItem(storageKey, JSON.stringify({ lastCorrectDate: dateKey, streak: nextStreak }));
  return nextStreak;
}

function renderChoiceButton(choice: DailyHandChoice, puzzle: DailyHandScenario): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'choice-button';
  button.dataset.choiceId = choice.id;
  button.textContent = choice.label;

  button.addEventListener('click', () => {
    const legal = isChoiceLegal(puzzle, choice);
    const correct = legal && choice.id === puzzle.correctChoiceId;
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.choice-button'));

    for (const choiceButton of buttons) {
      choiceButton.disabled = true;
      if (choiceButton.dataset.choiceId === puzzle.correctChoiceId) {
        choiceButton.style.borderColor = 'var(--green)';
      } else if (choiceButton === button && !correct) {
        choiceButton.style.borderColor = 'var(--red)';
      }
    }

    const result = requiredElement<HTMLElement>('puzzle-result');
    result.hidden = false;
    result.className = `callout ${correct ? 'success' : 'warning'}`;
    result.innerHTML = `
      <strong>${correct ? 'Scelta corretta.' : 'Non è la scelta migliore.'}</strong>
      <p>${puzzle.explanation}</p>
      <p><strong>Principio strategico:</strong> ${puzzle.lesson}</p>
    `;

    const streak = updateStreak(correct);
    requiredElement<HTMLElement>('puzzle-streak').textContent = streak > 0
      ? `Serie corretta: ${streak} ${streak === 1 ? 'giorno' : 'giorni'}`
      : 'La serie inizia con la prima risposta corretta.';

    trackEvent('daily_hand_answer', {
      scenario: puzzle.id,
      puzzle_date: dateKey,
      choice: choice.id,
      correct,
      legal,
    });
    trackEvent('daily_hand_complete', {
      scenario: puzzle.id,
      puzzle_date: dateKey,
      correct,
    });
  });

  return button;
}

async function sharePuzzle(): Promise<void> {
  const shareUrl = createCampaignUrl(
    `/mano-del-giorno/?date=${encodeURIComponent(dateKey)}`,
    'daily_hand',
    'share',
    'web_share',
  );
  const shareData = {
    title: `Mano del giorno — ${scenario.title}`,
    text: 'Qual è la giocata migliore a Sbarazzino?',
    url: shareUrl,
  };

  if (navigator.share) {
    await navigator.share(shareData);
  } else {
    await navigator.clipboard.writeText(shareUrl);
    requiredElement<HTMLButtonElement>('share-button').textContent = 'Link copiato';
  }

  trackEvent('daily_hand_share', {
    scenario: scenario.id,
    puzzle_date: dateKey,
    channel: navigator.share ? 'native' : 'clipboard',
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load ${src}`));
    image.src = src;
  });
}

async function drawCardRow(
  context: CanvasRenderingContext2D,
  cards: Card[],
  y: number,
  maxWidth: number,
): Promise<void> {
  const images = await Promise.all(cards.map(card => loadImage(cardImagePath(card))));
  const cardWidth = 230;
  const cardHeight = 380;
  const gap = 28;
  const totalWidth = cards.length * cardWidth + (cards.length - 1) * gap;
  const scale = Math.min(1, maxWidth / totalWidth);
  const width = cardWidth * scale;
  const height = cardHeight * scale;
  const scaledGap = gap * scale;
  const startX = (1080 - (cards.length * width + (cards.length - 1) * scaledGap)) / 2;

  images.forEach((image, index) => {
    context.drawImage(image, startX + index * (width + scaledGap), y, width, height);
  });
}

async function downloadSocialCard(): Promise<void> {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1920;
  const context = canvas.getContext('2d');
  if (!context) return;

  const gradient = context.createLinearGradient(0, 0, 0, 1920);
  gradient.addColorStop(0, '#174b2a');
  gradient.addColorStop(1, '#071a0e');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1080, 1920);

  context.textAlign = 'center';
  context.fillStyle = '#f5c542';
  context.font = '900 54px Georgia';
  context.fillText('SBARAZZINO', 540, 115);
  context.fillStyle = '#f7f4e9';
  context.font = '800 82px Georgia';
  context.fillText('MANO DEL GIORNO', 540, 220);
  context.fillStyle = '#b6c3b7';
  context.font = '500 34px system-ui';
  context.fillText(dateKey, 540, 280);

  context.fillStyle = '#f7f4e9';
  context.font = '800 50px system-ui';
  context.fillText(scenario.title, 540, 380);

  context.fillStyle = '#f5c542';
  context.font = '800 32px system-ui';
  context.fillText('CARTE SUL TAVOLO', 540, 470);
  await drawCardRow(context, scenario.table, 520, 940);

  context.fillStyle = '#f5c542';
  context.font = '800 32px system-ui';
  context.fillText('LA TUA MANO', 540, 1040);
  await drawCardRow(context, scenario.hand, 1090, 940);

  context.fillStyle = '#f7f4e9';
  context.font = '800 44px system-ui';
  context.fillText('Qual è la giocata migliore?', 540, 1600);
  context.fillStyle = '#b6c3b7';
  context.font = '500 30px system-ui';
  context.fillText('Rispondi su sbarazzino.it', 540, 1670);
  context.fillStyle = '#f5c542';
  context.font = '800 34px system-ui';
  context.fillText('sbarazzino.it/mano-del-giorno', 540, 1740);

  const link = document.createElement('a');
  link.download = `sbarazzino-mano-${dateKey}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();

  trackEvent('daily_hand_share', {
    scenario: scenario.id,
    puzzle_date: dateKey,
    channel: 'social_card',
  });
}

requiredElement<HTMLElement>('puzzle-date').textContent = new Intl.DateTimeFormat('it-IT', {
  dateStyle: 'long',
  timeZone: 'Europe/Rome',
}).format(new Date(`${dateKey}T12:00:00Z`));
requiredElement<HTMLElement>('puzzle-title').textContent = scenario.title;
requiredElement<HTMLElement>('puzzle-prompt').textContent = scenario.prompt;
renderCards(requiredElement<HTMLElement>('table-cards'), scenario.table);
renderCards(requiredElement<HTMLElement>('hand-cards'), scenario.hand);
requiredElement<HTMLElement>('puzzle-choices').replaceChildren(
  ...scenario.choices.map(choice => renderChoiceButton(choice, scenario)),
);

requiredElement<HTMLButtonElement>('share-button').addEventListener('click', () => {
  sharePuzzle().catch(error => console.warn('[DailyHand] Share failed:', error));
});
requiredElement<HTMLButtonElement>('download-card-button').addEventListener('click', () => {
  downloadSocialCard().catch(error => console.warn('[DailyHand] Card generation failed:', error));
});

trackEvent('daily_hand_start', {
  scenario: scenario.id,
  puzzle_date: dateKey,
});
